import { Injectable } from '@angular/core';

export interface PollingConfig<T> {
  // Time between polling cycles (milliseconds)
  intervalTime?: number;
  // Progressive delays for repeated actions (milliseconds)
  progressiveDelays?: number[];
  // Maximum number of retries before stopping (undefined means no limit)
  maxRetries?: number;
  // Condition to stop polling
  shouldStop: (data: T) => boolean;
  // Action to perform on each poll
  action: (data?: any) => Promise<T>;
  // Optional callback when polling stops
  onStop?: (data: T) => void;
}

@Injectable({
  providedIn: 'root'
})
export class PollingService {
  private intervals: Map<string, any> = new Map();

  // Default values
  private readonly DEFAULT_INTERVAL_TIME = 5000;
  private readonly DEFAULT_PROGRESSIVE_DELAYS = [1000, 2000, 3000, 5000, 10000, 15000];
  private readonly DEFAULT_MAX_RETRIES = 20;

  /**
   * Start polling with the provided configuration
   * @param key Unique identifier for this polling instance
   * @param config Polling configuration
   * @returns A function to manually stop polling
   */
  startPolling<T>(key: string, config: PollingConfig<T>) {
    // Stop any existing polling with same key
    this.stopPolling(key);

    // Apply default values if not provided
    const finalConfig = {
      ...config,
      intervalTime: config.intervalTime ?? this.DEFAULT_INTERVAL_TIME,
      progressiveDelays: config.progressiveDelays ?? this.DEFAULT_PROGRESSIVE_DELAYS,
      maxRetries: config.maxRetries ?? this.DEFAULT_MAX_RETRIES
    };

    let delayIndex = 0;
    let latestData: T;
    let isActionRunning = false;
    let retryCount = 0;

    const intervalId = setInterval(() => {
      // Skip if action is already running
      if (isActionRunning) return;

      // Check if we should stop based on data condition
      if (latestData && finalConfig.shouldStop(latestData)) {
        this.stopPolling(key);
        if (finalConfig.onStop) finalConfig.onStop(latestData);
        return;
      }

      // Check if we've reached max retries
      if (finalConfig.maxRetries !== undefined && retryCount >= finalConfig.maxRetries) {
        this.stopPolling(key);
        if (finalConfig.onStop) finalConfig.onStop(latestData);
        return;
      }

      // Schedule the next action with progressive delay
      setTimeout(async () => {
        isActionRunning = true;
        try {
          latestData = await finalConfig.action(latestData);

          // Increment delay index if not at max
          if (delayIndex < finalConfig.progressiveDelays.length - 1) {
            delayIndex++;
          }

          // Increment retry count
          retryCount++;
        } finally {
          isActionRunning = false;
        }
      }, finalConfig.progressiveDelays[delayIndex]);
    }, finalConfig.intervalTime);
    this.intervals.set(key, intervalId);

    // Return a function to stop polling
    return () => this.stopPolling(key);
  }

  /**
   * Stop polling for the given key
   */
  stopPolling(key: string) {
    if (this.intervals.has(key)) {
      clearInterval(this.intervals.get(key));
      this.intervals.delete(key);
    }
  }

  /**
   * Check if polling is active for the given key
   * @param key Unique identifier for the polling instance
   * @returns True if polling is active, false otherwise
   */
  isPolling(key: string): boolean {
    return this.intervals.has(key);
  }

  /**
   * Stop all active polling operations
   */
  stopAll() {
    this.intervals.forEach((id) => clearInterval(id));
    this.intervals.clear();
  }
}
